import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { serviceAPI } from '../services/api';
import Layout from '../components/Layout';

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchService();
  }, [id]);

  const fetchService = async () => {
    try {
      const data = await serviceAPI.getById(id);
      setService(data);
    } catch (error) {
      console.error('Error fetching service:', error);
      setError('Failed to load service details');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (filename) => {
    if (filename) {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://anesthetist-tracker-api.kaushalvaja3.workers.dev/api';
      window.open(`${apiUrl}/files/${filename}`, '_blank');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await serviceAPI.delete(id);
      navigate('/services', { 
        state: { message: 'Service deleted successfully' }
      });
    } catch (error) {
      console.error('Error deleting service:', error);
      setError('Failed to delete service: ' + (error.response?.data?.detail || error.message));
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading service details...</div>
        </div>
      </Layout>
    );
  }

  if (error || !service) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Service not found'}
          </div>
          <div className="mt-4">
            <Link to="/services" className="btn-secondary">
              ← Back to Services
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Service Details</h1>
            <p className="text-gray-600 mt-1">Service ID: {service.id}</p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <Link to="/services" className="btn-secondary flex items-center justify-center">
              ← Back to Services
            </Link>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="btn-danger flex items-center justify-center"
              disabled={deleting}
            >
              🗑️ Delete Service
            </button>
            <Link to="/services/new" className="btn-primary flex items-center justify-center">
              + New Service
            </Link>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirm Delete
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this service for <strong>{service.patient_name}</strong>? 
                This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary flex-1"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="btn-danger flex-1"
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Service Information Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Patient Information */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl mr-2">👤</span>
              Patient Information
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-500">Patient Name</label>
                <p className="text-lg font-medium text-gray-900">{service.patient_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Patient Number</label>
                <p className="text-lg font-medium text-gray-900">{service.patient_number}</p>
              </div>
            </div>
          </div>

          {/* Hospital Information */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl mr-2">🏥</span>
              Hospital Information
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-500">Hospital</label>
                <p className="text-lg font-medium text-gray-900">{service.hospital_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Hospital ID</label>
                <p className="text-lg font-medium text-gray-900">{service.hospital_id}</p>
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl mr-2">🩺</span>
              Service Details
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-500">Service Date</label>
                <p className="text-lg font-medium text-gray-900">
                  {new Date(service.service_date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Days of Service</label>
                <p className="text-lg font-medium text-gray-900">{service.days_of_service} day(s)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Type of Anesthesia</label>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  {service.anesthesia_type}
                </span>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl mr-2">💰</span>
              Financial Information
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-500">Amount Charged</label>
                <p className="text-2xl font-bold text-green-600">
                  ${service.amount_charged.toLocaleString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Rate per Day</label>
                <p className="text-lg font-medium text-gray-900">
                  ${(service.amount_charged / service.days_of_service).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Medication Information */}
        {service.medication_used && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl mr-2">💊</span>
              Medication Used
            </h2>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-900 whitespace-pre-wrap">{service.medication_used}</p>
            </div>
          </div>
        )}

        {/* Bill Information */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="text-2xl mr-2">📄</span>
            Bill & Documentation
          </h2>
          <div className="space-y-4">
            {service.bill_filename ? (
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📎</span>
                  <div>
                    <p className="font-medium text-green-800">Bill Attached</p>
                    <p className="text-sm text-green-600">{service.bill_filename}</p>
                  </div>
                </div>
                <button
                  onClick={() => downloadFile(service.bill_filename)}
                  className="btn-primary"
                >
                  Download
                </button>
              </div>
            ) : (
              <div className="flex items-center p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <span className="text-2xl mr-3">📄</span>
                <div>
                  <p className="font-medium text-gray-600">No Bill Attached</p>
                  <p className="text-sm text-gray-500">No file was uploaded for this service</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Metadata */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="text-2xl mr-2">ℹ️</span>
            Service Metadata
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Created Date</label>
              <p className="text-gray-900">
                {new Date(service.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500">Service ID</label>
              <p className="text-gray-900 font-mono">{service.id}</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4 pt-6">
          <Link to="/services" className="btn-secondary">
            ← Back to All Services
          </Link>
          <Link to="/dashboard" className="btn-secondary">
            Go to Dashboard
          </Link>
          <Link to="/services/new" className="btn-primary">
            Create New Service
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default ServiceDetail;